import { Button } from "@crew/ui/components/button";
import { Field, FieldError, FieldGroup, FieldLabel } from "@crew/ui/components/field";
import { Input } from "@crew/ui/components/input";
import { Link } from "@crew/ui/components/link";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api, errorHint } from "../../app/api";
import { isStandalone } from "../../app/install";
import { AuthCard } from "./auth-card";
import { startSession } from "./logout";
import { afterSignIn } from "./next";
import { HOME } from "../../app/home";

export function LoginPage() {
  const navigate = useNavigate();
  const { search } = useLocation();
  const client = useQueryClient();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const login = useMutation({
    mutationFn: () => api("POST", "/api/auth/login", { email, password }),
    onSuccess: async () => {
      await startSession(client);
      // Back to the page they opened signed out; the guards send an unpaid workspace on.
      navigate(afterSignIn(search, HOME), { replace: true });
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    login.mutate();
  };

  return (
    <AuthCard
      title="Sign in"
      description={
        // Installed on a home screen, the app keeps its own sign-in, apart from the browser's.
        isStandalone() ? "Sign in once here: the app keeps its own sign-in." : "Welcome back."
      }
      footer={
        <>
          New to Crew?{" "}
          <Link href="/signup" className="underline">
            Create a workspace
          </Link>
        </>
      }
    >
      <form onSubmit={submit}>
        <FieldGroup>
          <Field>
            <FieldLabel htmlFor="email">Email</FieldLabel>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </Field>
          <Field>
            <FieldLabel htmlFor="password">Password</FieldLabel>
            <Input
              id="password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </Field>
          {login.error ? <FieldError>{errorHint(login.error)}</FieldError> : null}
          <Button type="submit" disabled={login.isPending}>
            {login.isPending ? "Signing in…" : "Sign in"}
          </Button>
        </FieldGroup>
      </form>
    </AuthCard>
  );
}
